<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useAppMode } from "@/composables/useAppMode";
import { usePokerTableTotalPot } from "@/composables/usePokerTableTotalPot";
import { usePlayerStacks } from "@/composables/usePlayerStacks";
import { useSeatCardGroup } from "@/composables/useSeatCardGroup";

import PlayingCard from "@/components/table/PlayingCard.vue";
import PlayerChipStack from "@/components/table/chips/PlayerStack.vue";
import PokerTableTotalPot from "./PokerTableTotalPot.vue";
import TablePots from "@/components/table/TablePots.vue";

import Typography from "@/components/ui/typography/Typography.vue";

import dealerImage from "@/assets/dealer.png";

import { useGameState, GameState } from "@/stores/gameState";
import {
  seatConfigByCount,
  dealerChipLocationsByCount,
  chipStackLocationsByCount,
} from "@/constants";
import { throttle } from "@/lib/utils";

const { mode } = useAppMode();

const {
  roomState,
  gameState,
  isGameStarted,
  player,
  dealerIndex,
  totalPot,
  playerCardsVisible,
  winners,
} = storeToRefs(useGameState());
const { isCardLost } = useGameState();
const { playerStacks } = usePlayerStacks(roomState);
const { visiblePlayerCards, isDealing } = useSeatCardGroup({
  loggedInPlayer: player,
  isPlayerCardsVisible: playerCardsVisible,
  roomState,
  winnerIds: winners,
});

const getPlayerStackPosBySeatIdx = (seatIdx: number) => {
  return chipCountsLocations.value[index(seatIdx)];
};

const { winnersDisplayData } = usePokerTableTotalPot({
  playerStackPosFn: getPlayerStackPosBySeatIdx,
  winnersWithPayout: computed(
    () => roomState.value.hand?.winnersWithPayout || [],
  ),
});

const seatCount = computed(() => roomState.value.max_players);
const communityCards = computed(
  () => roomState.value.hand?.communityCards ?? [],
);
const winnerCards = computed(() => roomState.value.hand?.winnerCards ?? []);

const seatConfig = computed(
  () => seatConfigByCount[mode.value][seatCount.value],
);
const dealerChipLocations = computed(
  () => dealerChipLocationsByCount[mode.value][seatCount.value],
);
const chipCountsLocations = computed(
  () => chipStackLocationsByCount[mode.value][seatCount.value],
);

const tableRotation = computed(() => {
  if (!isGameStarted.value) {
    return 0;
  }

  const myIndex = player.value
    ? roomState.value.seats.findIndex(
        (seat) => seat?.playerId === player.value!.playerId,
      )
    : -1;

  if (myIndex === -1) {
    return 0;
  }

  return -myIndex;
});

const index = (idx: number) =>
  (idx + tableRotation.value + seatCount.value) % seatCount.value;

const delayedAnimation = ref(true);
const resetDelayedAnimation = throttle(
  () => (delayedAnimation.value = false),
  3000,
);
resetDelayedAnimation();
watch(
  () => communityCards.value.length,
  (currCount, prevCount) => {
    if (currCount - prevCount > 1) {
      delayedAnimation.value = true;
      resetDelayedAnimation();
    }
  },
);

const lostCards = ref<string[]>([]);
watch(
  () => [winnerCards.value.length, delayedAnimation.value],
  () => {
    if (!delayedAnimation.value) {
      lostCards.value = communityCards.value.filter((card) => isCardLost(card));
    }
  },
);

const tableSettings = computed(
  () =>
    `NLHE ${roomState.value.gameConfig?.blinds.join("/")}${roomState.value.gameConfig?.antes ? ` (${roomState.value.gameConfig.antes})` : ""}`,
);
const skipCardsAnimation = computed(() =>
  ["flop", "turn", "river"].indexOf(roomState.value.gameConfig?.skipTo),
);

const NEXT_HAND_VISIBILITY = 2 * 1000;
const isNewHandStarting = ref(false);
const resetNewHandStarting = throttle(
  () => (isNewHandStarting.value = false),
  NEXT_HAND_VISIBILITY,
);
watch(
  () => roomState.value.handNumber,
  (newValue, oldValue) => {
    isNewHandStarting.value = true;
    resetNewHandStarting();
  },
);
</script>

<template>
  <div
    class="table"
    :class="{
      'table--away':
        gameState === GameState.ERROR ||
        gameState === GameState.DISCONNECTED ||
        gameState === GameState.INIT,
      'table--desktop': mode === 'desktop',
      'table--mobile': mode === 'mobile',
    }"
  >
    <div id="table_ID" :class="`table__game-zone table__game-zone--${mode}`">
      <PokerTableTotalPot
        :app-view-mode="mode"
        :pot="totalPot"
        :winners="winnersDisplayData"
      />
      <Typography
        size="text-12-18"
        class="absolute left-[50%] top-[44%] mt-[5px] -translate-x-1/2 whitespace-nowrap text-center opacity-[14%] lg:top-[75%]"
        >{{ tableSettings }}</Typography
      >
      <div
        v-if="isGameStarted"
        class="table__community-cards"
        :class="`table__community-cards--${mode}`"
      >
        <div class="table__flop-container">
          <div class="table__community-card-wrapper" v-for="() in 3"></div>
          <transition
            appear
            name="table__flop"
            :class="{ 'table__flop--skip-animation': skipCardsAnimation >= 0 }"
          >
            <div v-if="communityCards.length >= 3" class="table__flop">
              <playing-card
                v-for="(card, idx) in communityCards.slice(0, 3)"
                :key="idx"
                class="table__community-card"
                :value="card"
                :lost="lostCards.includes(card)"
              />
            </div>
          </transition>
        </div>
        <div class="table__turn-container">
          <div class="table__community-card-wrapper" />
          <transition
            appear
            :name="`table__turn${delayedAnimation ? '--delayed' : ''}`"
            :class="{ 'table__turn--skip-animation': skipCardsAnimation >= 1 }"
          >
            <playing-card
              v-if="communityCards.length >= 4"
              class="table__community-card"
              :value="communityCards[3]"
              :lost="lostCards.includes(communityCards[3])"
            />
          </transition>
        </div>
        <div class="table__river-container">
          <div class="table__community-card-wrapper" />
          <transition
            appear
            :name="`table__river${delayedAnimation ? '--delayed' : ''}`"
            :class="{ 'table__river--skip-animation': skipCardsAnimation >= 2 }"
          >
            <playing-card
              v-if="communityCards.length === 5"
              class="table__community-card"
              :value="communityCards[4]"
              :lost="lostCards.includes(communityCards[4])"
            />
          </transition>
        </div>
      </div>

      <table-pots class="table__pots" :class="`table__pots--${mode}`" />

      <img
        :src="dealerImage"
        class="table__button"
        :class="{
          [`table__button--${mode}`]: true,
          'table__button--self':
            index(dealerIndex) === 0 &&
            roomState.seats[dealerIndex]?.playerId === player?.playerId,
        }"
        :style="{
          left: `${dealerChipLocations[index(dealerIndex)].left}%`,
          top: `${dealerChipLocations[index(dealerIndex)].top}%`,
        }"
      />

      <div class="table__hand-strength">
        <slot name="hand-strength" />
      </div>
      <div class="table__self-emoji" :class="`table__self-emoji--${mode}`" />

      <div
        v-if="$slots['addon-bottom'] && !isGameStarted"
        class="table__addon-bottom"
        :class="`table__addon-bottom--${mode}`"
      >
        <slot name="addon-bottom" />
      </div>

      <transition name="table__bomb-pot-banner" mode="out-in">
        <img
          v-if="isNewHandStarting && roomState.flavors.includes('bombPot')"
          class="table__bomb-pot-banner"
          :class="`table__bomb-pot-banner--${mode}`"
          src="@/assets/bomb-pot-banner.png"
        />
      </transition>

      <template v-for="(stack, idx) in playerStacks" :key="idx">
        <player-chip-stack
          :style="{
            left: `${chipCountsLocations[index(idx)].left}%`,
            top: `${chipCountsLocations[index(idx)].top}%`,
          }"
          class="table__stack"
          :class="{
            [`table__stack--${mode}`]: true,
            'table__stack--self':
              roomState.seats[idx]?.playerId === player?.playerId,
          }"
          :value="stack"
          variant="light"
          layout="vertical"
        />
      </template>
      <template v-for="(_, idx) in roomState.seats" :key="idx">
        <transition name="table__seat" mode="out-in">
          <slot
            name="table-seat"
            :player-cards="
              visiblePlayerCards[roomState.seats[idx]?.playerId]
            "
            :is-dealing="isDealing"
            :seat="roomState.seats[idx]"
            :config="seatConfig[index(idx)]"
            :index="idx"
            :position="index(idx)"
          ></slot>
        </transition>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.table {
  position: relative;
  width: calc(
    var(--table-size) * calc(min(1 / var(--table-image-ratio) * 1dvh, 1dvw))
  );
  height: calc(
    var(--table-size) * calc(min(var(--table-image-ratio) * 1dvw, 1dvh))
  );
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: all 0.5s ease-out;

  --table-image-ratio: calc(
    var(--table-image-height) / var(--table-image-width)
  );
  --table-gaming-zone-area-ratio: calc(
    var(--table-gaming-zone-area-height) / var(--table-gaming-zone-area-width)
  );
  --tw: calc(min(1 / var(--table-image-ratio) * 1dvh, 1dvw));
  --th: calc(min(var(--table-image-ratio) * 1dvw, 1dvh));
  --table-width: calc(var(--table-size) * var(--tw));
  --table-height: calc(var(--table-size) * var(--th));
  --table-gaming-zone-left: calc(
    (var(--table-image-width) - var(--table-gaming-zone-area-width)) / 2
  );
  --trw: calc(
    min(
      1 / var(--table-image-ratio) * 1dvh * var(--table-size) / 100,
      1dvw * var(--table-size) / 100
    )
  );
  --trh: calc(
    min(
      var(--table-image-ratio) * 1dvw * var(--table-size) / 100,
      1dvh * var(--table-size) / 100
    )
  );
  --tgw: calc(
    var(--table-gaming-zone-area-width) / var(--table-image-width) * var(--trw)
  );
  --tgh: calc(
    var(--table-gaming-zone-area-height) / var(--table-image-height) *
      var(--trh)
  );
  --card-width: 55;
  --card-height: 75;
  --card-gap: calc(var(--card-table-width) * 0.75);
  --card-table-height: calc(
    var(--card-table-width) * var(--card-height) / var(--card-width)
  );

  &--desktop {
    background-image: url("@/assets/table.webp");
    margin-top: 50px;
    --table-size: 50;
    --table-image-width: 2099;
    --table-image-height: 1047;
    --table-gaming-zone-area-width: 1960;
    --table-gaming-zone-area-height: 900;
    --table-gaming-zone-top: 30;
    --card-table-width: calc(var(--tgw) * 7);
    --card-player-width: var(--card-table-width);
    --card-player-height: var(--card-table-height);
    --table-chip-width: calc(var(--tgw) * 3 * 1.11);
    --table-chip-height: calc(var(--tgw) * 3);

    @media (min-width: 1200px) {
      --table-size: 45;
    }
  }

  &--mobile {
    background-image: url("@/assets/table-mobile-standard.webp");
    background-size: contain;
    --table-size: 73;
    --table-image-width: 1125;
    --table-image-height: 2436;
    --table-gaming-zone-area-width: 1033;
    --table-gaming-zone-area-height: 1720;
    --table-gaming-zone-top: 300;
    --card-table-width: 30px;
    --card-player-width: calc(0.4 * var(--card-table-width));
    --card-player-height: calc(0.4 * var(--card-table-height));
    --table-chip-width: calc(var(--tgw) * 6 * 1.11);
    --table-chip-height: calc(var(--tgw) * 6);
    margin-top: -80px;
    margin-bottom: -15px;
    z-index: 2;
  }

  &--away {
    filter: grayscale(100%);
  }

  &__game-zone {
    position: absolute;
    width: calc(var(--tgw) * 100);
    height: calc(var(--tgh) * 100);
    top: calc(var(--table-gaming-zone-top) / var(--table-image-height) * 100%);
    left: calc(var(--table-gaming-zone-left) / var(--table-image-width) * 100%);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background-size: 28%;
      background-position: 50% 27%;
      background-repeat: no-repeat;
      opacity: 0.14;
      background-image: url("@/assets/logo-v3.svg");
    }

    &--mobile::before {
      background-position: 50% 40%;
    }

    &--desktop::before {
      background-size: 12%;
      background-position: 50% 24%;
    }
  }

  &__seat {
    position: absolute;
    transition: all 0.25s ease-out;

    &--desktop {
      width: 19.6%;
      height: 17.39%;
    }

    &--mobile {
      width: 80px;
      height: 47px;
    }

    &-enter-active,
    &-leave-active {
      transition: all 0.25s ease-out;
    }

    &-enter-from,
    &-leave-to {
      transform: translate(-50%, 0);
      opacity: 0;
    }
  }

  &__addon-bottom {
    &--desktop {
      position: absolute;
      bottom: 37%;
      left: 50%;
      width: 50%;
      transform: translateX(-50%);
    }

    &--mobile {
      position: absolute;
      bottom: 30%;
      left: 50%;
      width: 56%;
      transform: translateX(-50%);
    }
  }

  &__button {
    position: absolute;
    width: var(--table-chip-width);
    transform: translate(-50%, -50%);
    transition: all 0.25s ease-out;
  }

  &__total-pot {
    position: absolute;
    left: 50%;
    top: 31%;
    display: flex;
    transform: translateX(-50%);
    font-size: calc(3.2 * var(--tgh));
    color: #fff;
    padding: calc(0.526 * var(--tgh)) calc(0.842 * var(--tgw));

    &-count {
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.35);
      padding: 0 5px;
    }

    &--mobile {
      font-size: calc(2.04 * var(--tgh));
      line-height: 120%;
      flex-direction: column;
      align-items: center;
      background: none;
      top: 54%;
    }

    &--desktop {
      .chip-stack__stack {
        top: 0;
        .chip-stack__stack-chip {
          width: calc(4.2 * var(--tgw));
          height: calc(4.2 * var(--tgw));
        }
      }

      .chip-stack__value {
        font-size: calc(6.5 * var(--tgh));
      }
    }
  }

  &__community-card {
    --card-width: var(--card-table-width);
    --card-height: var(--card-table-height);
    background-color: white;
    flex-shrink: 0;
  }

  &__community-card-wrapper {
    position: absolute;
    border: 1px solid white;
    opacity: 0.2;
    width: var(--card-table-width);
    height: var(--card-table-height);
    border-radius: 6px;
    transform: scale(1.15, 1.1);

    &:nth-of-type(2) {
      margin-left: calc(var(--card-table-width) + var(--tw) * 0.7);
      @media screen and (max-width: 640px) {
        margin-left: calc(var(--card-table-width) + var(--tw) * 0.8);
      }
    }

    &:nth-of-type(3) {
      margin-left: calc(var(--card-table-width) * 2 + var(--tw) * 1.32);
      @media screen and (max-width: 640px) {
        margin-left: calc(var(--card-table-width) * 2 + var(--tw) * 1.42);
      }
    }


  }

  &__community-cards {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: calc(0.7125 * var(--tw));
    height: var(--card-table-height);

    @media screen and (max-width: 640px) {
      gap: calc(1 * var(--tw));
    }

    &--desktop { top: 53.5%; }
    &--mobile  { top: 65%; }
  }

  &__stack {
    position: absolute;
  }

  &__pots {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 72%;
    left: 50%;
    transform: translate(-50%, -50%);
    gap: calc(0.842 * var(--tgh));

    .chip-stack .chip-stack__value {
      min-width: 0;
    }

    &--mobile {
      top: 74%;
      max-width: calc(var(--tgw) * 70);

      &-row {
        flex-wrap: wrap;
      }
    }
  }

  &__pots-row {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__pot-separator::after {
    content: " ";
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc(0.475 * var(--tw));
    height: calc(0.475 * var(--tw));
    border-radius: 50%;
    background: #fcfcfc;
    opacity: 0.6;
    margin: 0 calc(0.2 * var(--tw));
    margin-bottom: 1px;
  }

  &__flop-container {
    width: calc(var(--card-table-width) * 3 + var(--tw) * 1.4);
  }
  &__turn-container,
  &__river-container {
    width: var(--card-table-width);
  }

  &__flop {
    display: flex;
    justify-content: space-between;
    gap: calc(0.7125 * var(--tw));
    height: var(--card-table-height);
    position: relative;

    &-enter-active {
      transition:
        width 0.5s ease-out,
        opacity 0.5s ease-out,
        transform 0.5s ease-out;
      transition-delay: 0.5s, 0s, 0s;
    }

    & *:nth-child(1) { position: absolute; left: 0; }
    & *:nth-child(2) {
      position: absolute;
      left: calc(50% - var(--card-table-width) / 2);

      @media screen and (max-width: 640px) {
        left: calc(49.8% - var(--card-table-width) / 2)
      }
    }
    & *:nth-child(3) {
      position: absolute;
      right: 0;

      @media screen and (max-width: 640px) {
        right: -1%;
      }

    }

    &-enter-from {
      opacity: 0.8;
      width: var(--card-table-width);
      transform: translate(-50%, 50%) scale(0.9);
    }

    &-enter-to {
      width: 100%;
    }
    &-leave-active,
    &-leave-to .card {
      transition:
        opacity 0.7s ease-out,
        transform 0.7s ease-out;
    }

    &-leave-to .card {
      background-image: url("@/assets/cards/back.svg");
      background-size: cover;
    }
  }

  &__turn,
  &__river {
    &-enter-active {
      transition:
        opacity 0.7s ease-out,
        transform 0.7s ease-out;
    }
    &-enter-from {
      opacity: 0;
      transform: translate(0%, 90%) scale(0.6);
    }
    &-leave-active {
      transition:
        opacity 0.7s ease-out,
        transform 0.7s ease-out;
    }
    &-leave-to {
      opacity: 0.4;
      transform: translate(0%, 50%);
    }
    &--delayed-enter-active {
      transition:
        opacity 0.7s ease-out,
        transform 0.7s ease-out;
      transition-delay: if(&__turn, 1.4s, 2.1s);
    }
    &--delayed-enter-from {
      opacity: 0;
      transform: translate(0%, 90%) scale(0.6);
    }
    &--delayed-leave-active {
      transition:
        opacity 0.7s ease-out,
        transform 0.7s ease-out;
    }
    &--delayed-leave-to {
      background-image: url("@/assets/cards/back.svg");
      background-size: cover;
      opacity: 0.4;
      transform: translate(0%, 50%);
    }
  }

  &__hand-strength {
    position: absolute;
    left: 50%;
    top: 88%;
    opacity: 0.25;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    transform: translateX(-50%);
  }

  &__self-emoji {
    position: absolute;
    left: 50%;
    top: 40%;
    width: calc(30 * var(--tw));
    height: calc(30 * var(--th));
    transform: translateX(-50%);

    &--mobile {
      top: 80%;
    }
  }

  &__bomb-pot-banner {
    position: absolute;
    left: 50%;
    top: 60%;
    z-index: 999;
    max-width: 100%;
    transform: translate(-50%, -50%);
    background-image: url("@/assets/bomb-pot-banner.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &--desktop {
      max-width: 60%;
    }

    &-enter-active,
    &-leave-active {
      transition: all 0.25s ease-in-out;
    }

    &-enter-from {
      transform: translate(-200%, -50%);
      opacity: 0;
    }

    &-leave-to {
      transform: translate(100%, -50%);
      opacity: 0;
    }
  }
}
</style>
